<!-- app.vue -->
<template>
  <UApp>
    <NuxtLayout>
      <NuxtPage/>
    </NuxtLayout>
  </UApp>
</template>

<script setup lang="ts">

const userStore = useUserStore()
const uiStore = useUiStore()
const catalogStore = useCatalogStore()

// Lógica Client-Side
if (import.meta.client) {
  // --- LÓGICA DO CATÁLOGO COM FALLBACK PARA MOCK ---

  const initCatalog = async () => {
    let moodleCatalog = (window as any).MOODLE_CATALOG;

    // Se NÃO veio do PHP e estamos em DEV, carrega o Mock dinamicamente
    if (!moodleCatalog && import.meta.dev) {
      console.info("🛠️ DEV MODE: Nenhum catálogo injetado. Carregando Mock...");

      const module = await import('../mocks/moodleCatalogLarge');
      moodleCatalog = module.moodleCatalogLarge;
    }

    // Se temos catálogo (seja do PHP ou do Mock), salvamos na Store
    if (moodleCatalog) {
      console.log("Catálogo carregado:", moodleCatalog);
      // Se o mock já vier como objeto limpo (sem stringify), o setCatalog aceita
      catalogStore.setCatalog(moodleCatalog);
    } else {
      console.warn("Nenhum catálogo disponível.");
    }
  };

  initCatalog();

  const initUser = async () => {
    let moodleUser = (window as any).MOODLE_USER;

    // Se NÃO veio do PHP e estamos em DEV, carrega o Mock dinamicamente
    if (!moodleUser && import.meta.dev) {
      console.info("🛠️ DEV MODE: Nenhum usuário injetado. Carregando Mock dinâmico...");
      // Ajuste o caminho relativo conforme a estrutura de pastas do seu app.vue
      const module = await import('../mocks/moodleUser');
      moodleUser = module.moodleUser;
    }

    if (moodleUser) {
      // console.log("Usuário Moodle carregado:", moodleUser.username);
      // Salva no Pinia
      userStore.setUser(moodleUser);
      await userStore.fetchUnreadCounts()

      // --- RESGATE DO TOKEN ---
      // Caso o Moodle tenha apagado o LocalStorage, resgatamos do Cookie
      const wsCookie = useCookie<{ id: number, token: string } | null>('nuxt_ws_token');

      // Injeção do Mock do wsToken via Cookie em DEV
      if (!wsCookie.value && import.meta.dev) {
        console.info("🛠️ DEV MODE: Nenhum wsToken encontrado. Injetando Mock no cookie...");
        const wsModule = await import('../mocks/moodleWsToken');
        // Já criamos o cookie associado ao ID do mock para cair na "Situação B"
        wsCookie.value = {id: moodleUser.id, token: wsModule.moodleWsTokenMock};
      }

      if (wsCookie.value) {
        // A sesskey atual (do PHP ou do Mock) para operar o cadeado
        const currentSesskey = (window as any).MOODLE_SESSKEY || uiStore.sesskey;

        // Situação A: Token veio da transição de login agora mesmo (id do cookie era 0)
        if (wsCookie.value.id === 0) {
          // 1. Salva na memória do Pinia para uso imediato
          userStore.wsToken = wsCookie.value.token;
          // 2. Criptografa usando a nova sesskey definitiva e atualiza o cookie (Protegido!)
          if (currentSesskey) {
            const encryptedToken = await encryptTokenWithSesskey(wsCookie.value.token, currentSesskey);
            wsCookie.value = {id: moodleUser.id, token: encryptedToken};
            console.log("🔒 Token WS protegido com sucesso na sessão.");
          }
        }
        // Situação B: O Token já estava consolidado para o usuário correto
        else if (wsCookie.value.id === moodleUser.id) {
          // Tenta abrir o cadeado do cookie usando a sesskey atual
          const decryptedToken = currentSesskey
              ? await decryptTokenWithSesskey(wsCookie.value.token, currentSesskey)
              : null;

          if (decryptedToken) {
            // Sessão válida! O Token voltou a ser texto plano em memória
            userStore.wsToken = decryptedToken;
          } else {
            // FALHA NA SEGURANÇA:
            // O usuário fez logout no Moodle em outra aba, a sessão venceu,
            // ou alguém copiou o cookie para outra máquina.
            console.warn("🚫 Falha ao descriptografar token WS. Sessão vencida ou inválida.");
            wsCookie.value = null; // Destrói o cookie inútil
            // Nota: userStore.wsToken continuará vazio, logo isLoggedIn será false.
          }
        }
        // Situação C: O Token no cookie pertence a outro usuário (sujeira) -> Limpa
        else {
          wsCookie.value = null;
        }
      }

      // Verificação de segurança (Troca de senha forçada)
      if (userStore.prefs.forcePasswordChange) {
        // Pequeno timeout para garantir que o Nuxt montou antes do alert
        setTimeout(() => {
          alert('Por segurança, você precisa alterar sua senha antes de continuar.');
          window.location.href = '/login/change_password.php';
        }, 500);
      }
    } else {
      // Se MOODLE_USER for null ou undefined, assumimos visitante
      console.log("Nenhum dado de usuário injetado. Assumindo visitante.");
      // Opcional: userStore.$reset() ou setar isGuest = true manualmente
      userStore.isGuest = true;
    }
  };

  const initSessKey = async () => {
    const ui_store = useUiStore()
    let moodleSesskey = (window as any).MOODLE_SESSKEY;

    // Se NÃO veio do PHP e estamos em DEV, carrega o Mock dinamicamente
    if (!moodleSesskey && import.meta.dev) {
      console.info("🛠️ DEV MODE: Nenhuma Sesskey injetada. Carregando Mock dinâmico...");
      const module = await import('../mocks/moodleSesskey');
      moodleSesskey = module.moodleSesskeyMock;
    }

    if (moodleSesskey) {
      ui_store.sesskey = moodleSesskey;
      console.log("Sesskey configurada:", ui_store.sesskey);
    }

    await nextTick();
  };

  const initAlerts = () => {
    let moodleAlerts = (window as any).MOODLE_ALERTS;

    if (moodleAlerts && Array.isArray(moodleAlerts)) {
      uiStore.setSystemAlerts(moodleAlerts);
    }
  };

  // Executa as inicializações
  initUser();
  initSessKey();
  initAlerts();
}
</script>